<template>
  <div id="secure-dataflow-live-map"></div>
</template>

<script>
import Mapbox from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import moment from "moment-timezone";
import _ from "lodash";

import {
  MAP_STYLE_DEFAULT,
  MAP_STYLE_SATELLITE,
  MAP_DEFAULT_POSITION
} from "@/config/const";

import SecureDataflowService from "@/services/secureDataflowService";

// mixins
import {
  ColorMixin,
  MappingTableMixin,
  StoreMonitoringVesselManeuveringMixin,
  StoreMenuMixin,
  StoreCommonsMixin
} from "@/mixins/addon/secure-dataflow";

const MAP_STYLE_SATELLITE_SECURE_DATAFLOW =
  "mapbox://styles/aidea/cknfhxsgr3w0p17tfqqwu405n";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
const epVesselSecureDataflowChannel = AiseaApiFactory.get(
  "enterpriseVesselSecureDataflowChannel"
);
import { createNamespacedHelpers } from "vuex";
const { mapGetters } = createNamespacedHelpers("monitoringVesselEngine");


export default {
  name: "secure-dataflow-live-map",
  mixins: [
    ColorMixin,
    MappingTableMixin,
    StoreMenuMixin,
    StoreMonitoringVesselManeuveringMixin,
    StoreCommonsMixin
  ],
  data() {
    return {
      intervalIds: [],
      initialized: false,
      resetMap: false,
      map: null,
      mapEvent: null,
      accessToken: process.env.VUE_APP_MAPBOX_TOKEN,
      layerIDs: [],
      monitoringVesselCircle: null,
      popupForMonitoringVessel: null,
      popupForGhost: null,
      trackLineFeatureCollection: null,
      beforeDisplayGhostDatetime: null,
      mapStyleType: 1,
      imageDefault: require("@/assets/images/pages/map/streetmap.png"),
      imageSatellite: require("@/assets/images/pages/map/sattellite.png"),
      isShowPopupForMonitoringVessel: false,
      popupForSeaRoutes: [],
      currentOperationMode: null,
      zoomLevel: null,
    };
  },
  watch: {
    jumpTo(newVal, oldVal) {
      if (newVal !== null) {
        this.map.jumpTo(newVal);
        if (newVal.routeNumber) {
          this.showPopupForSeaRoute(newVal.routeNumber);
        }
      }
    },
    trackDisplayInterval(newVal) {
      if (newVal !== null) {
        this.setVisibilityGhost();
      }
    },
    channelDataManeuvering(newManeuveringData, oldManeuveringData) {
      if (this.initialized) {
        if (newManeuveringData !== null) {
          this.updateMonitoringVesselPositionSource(false);
        }
      } else {
        if (!this.map) {
          this.init();
        }
      }
    },
    nextArrivalPoint(newVal, oldVal) {
      if (newVal !== null) {
        this.updateSeaRoute();
      }
    },
    isDisplayOtherVessels() {
      this.setVisibilityOtherVessels();
    },
    trackPositions(newVal, oldVal) {
      if (newVal.length !== 0) {
        if (this.trackHistories !== null) {
          let copyTrackHistories = _.cloneDeep(this.trackHistories)
          this.SET_TRACK_POSITIONS(copyTrackHistories.concat(newVal))
          this.SET_TRACK_HISTORIES(null)
        } else {
          this.updateTrackSource();
        }
      }
    },
    otherVesselsPosition(newVal, oldVal) {
      if(newVal === null) return
      if (newVal.length !== 0) {
        this.updateOtherVessels();
      } else {
        this.clearOtherVessels();
      }
    },
    ghostPositions(newVal, oldVal) {
      if (newVal.length !== 0) {
        this.updateGhostSymbolSource();
      }
    },
    onAlertSummaryNotification(newVal, oldVal) {
      this.updateMapBoxCtrlStyle();
    },
    isArrivedPoint() {
      this.updateSeaRoute();
    }
  },
  computed: {
    ...mapGetters(["getVersion"]),
  },
  methods: {
    init() {
      // this.$vs.loading({
      //   container: "#secure-dataflow-live-map",
      //   scale: 0.6
      // });
      try {
        Mapbox.accessToken = this.accessToken;
        let geoPoint = this.getMonitoringVesselGeoPoint;
        let vesselInfo = this.getMonitoringVesselInfo;
        let center = [geoPoint.longitude, geoPoint.latitude];
        this.map = new Mapbox.Map({
          attributionControl: false,
          container: "secure-dataflow-live-map",
          style: MAP_STYLE_SATELLITE_SECURE_DATAFLOW,
          center: center,
          zoom: this.zoomLevel || this.getZoomLevelByOperationMode(vesselInfo.operationMode),
          doubleClickZoom: false,
          maxZoom: 19,
          localIdeographFontFamily: "'Noto Sans', sans-serif"
        });
        this.currentOperationMode = vesselInfo.operationMode

        this.crateControls();

        let self = this;
        this.map.on("load", () => {
          setTimeout( () => {
            this.$emit("closeLoading");
          }, 5000)
        });
        this.map.on("style.load", async () => {
          await self.cleanup()
          self.addSources();
          self.addLayers();
          await self.addOtherVessels();
          self.bindLayerClick();
          self.setPointerStyle();
          self.updateMonitoringVesselPositionSource(false);
          self.updateSeaRoute();
          self.setVirtualQuay();
          self.updateMapBoxCtrlStyle();
          self.setIntervalUpdateanHandlingData();
        });
      } catch (e) {
        this.$emit("closeLoading");
      }
    },

    closeLoading() {
      if (!this.initialized) {
        this.initialized = true;
      }
      this.$emit("closeLoading");
    },

    crateControls() {
      // スケールコントロール
      let nav = new Mapbox.NavigationControl();
      this.map.addControl(nav, "bottom-right");
      let scale = new Mapbox.ScaleControl({
        maxWidth: 80,
        unit: "imperial"
      });
      this.map.addControl(scale);
      scale.setUnit("metric");

      // 船舶ロケーションボタン
      const createVesselLocateButton = onclick => {
        const button = document.createElement("button");
        button.setAttribute("type", "button");
        button.classList.add("mapboxgl-ctrl-geolocate");
        button.appendChild(document.createElement("span"));
        button.children[0].classList.add("mapboxgl-ctrl-icon");
        button.addEventListener("click", onclick);
        return button;
      };
      const vesselLocateButton = createVesselLocateButton(ev => {
        let geoPoint = this.getMonitoringVesselGeoPoint;
        let vesselInfo = this.getMonitoringVesselInfo;
        this.map.jumpTo({
          center: [geoPoint.longitude, geoPoint.latitude],
          zoom: this.getZoomLevelByOperationMode(vesselInfo.operationMode),
        });
      });
      const vesselLocateControl = {
        onAdd: map => {
          this._container = document.createElement("div");
          this._container.classList.add("mapboxgl-ctrl", "mapboxgl-ctrl-group");
          this._container.classList.add("custom-control");
          this._container.appendChild(vesselLocateButton);
          return this._container;
        },
        getDefaultPosition: () => {
          return "bottom-right";
        },
        onRemove: () => {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
      };
      this.map.addControl(vesselLocateControl);

      // マップスタイル変更ボタン
      const createMapStyleChangerButton = onclick => {
        const button = document.createElement("button");
        button.setAttribute("type", "button");
        button.classList.add("mapboxgl-ctrl-map-style-changer");
        button.appendChild(document.createElement("img"));
        button.children[0].classList.add("mapboxgl-ctrl-icon");
        button.children[0].src = this.imageDefault;
        button.addEventListener("click", onclick);
        return button;
      };

      const mapStyleChangerButton = createMapStyleChangerButton(ev => {
        this.updateMapStyle();
      });

      const mapStyleChangerControl = {
        onAdd: map => {
          this._container = document.createElement("div");
          this._container.classList.add("mapboxgl-ctrl", "mapboxgl-ctrl-group");
          this._container.classList.add("custom-control");
          this._container.appendChild(mapStyleChangerButton);
          return this._container;
        },
        getDefaultPosition: () => {
          return "bottom-right";
        },
        onRemove: () => {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
      };
      this.map.addControl(mapStyleChangerControl);

      // 緯度経度
      const geoInfo = {
        onAdd(map) {
          this._map = map;
          this._container = document.createElement("div");
          this._container.setAttribute("id", "lng-lat-info");
          this._container.className = "mapboxgl-ctrl sdf-text-12-400-white";
          return this._container;
        },
        getDefaultPosition: () => {
          return "top-right";
        },
        onRemove() {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
      };
      this.map.addControl(geoInfo);
      document.getElementById("lng-lat-info").innerHTML =
        `Lat: ${this.map.getCenter().lat}` +
        "<br />" +
        `Lng: ${this.map.getCenter().lng}`;

      this.map.on("mousemove", e => {
        document.getElementById(
          "lng-lat-info"
        ).innerHTML = document.getElementById("lng-lat-info").innerHTML =
          `Lat: ${e.lngLat.lat}` + "<br />" + `Lng: ${e.lngLat.lng}`;
      });

      // ズーム値
      const zoomInfo = {
        onAdd(map) {
          this._map = map;
          this._container = document.createElement("div");
          this._container.setAttribute("id", "zoom-info");
          this._container.className = "mapboxgl-ctrl sdf-text-12-400-white";
          return this._container;
        },
        getDefaultPosition: () => {
          return "top-right";
        },
        onRemove() {
          this._container.parentNode.removeChild(this._container);
          this._map = undefined;
        }
      };
      this.map.addControl(zoomInfo);
      document.getElementById(
        "zoom-info"
      ).innerHTML = `Zoom: ${this.map.getZoom().toFixed(0)}`;

      this.map.on("zoomend", e => {
        document.getElementById(
          "zoom-info"
        ).innerHTML = `Zoom: ${this.map.getZoom().toFixed(0)}`;

        this.updateMonitoringVesselPositionSource(true);
        this.updateGhostSymbolSource();

        const mapInfo = {
          zoomLevel: this.map.getZoom()
        };
        SecureDataflowService.saveMap(mapInfo);
      });
    },
    addSources() {
      // 航路標識ソース
      this.map.addSource("sdfNavigationalAidsSource", {
        type: "vector",
        url: "mapbox://aidea.aisea_navigational_aids"
      });

      // 航路ソース
      this.map.addSource("sdfTrafficRouteSource", {
        type: "vector",
        url: "mapbox://aidea.traffic_routes"
      });

      // 監視船のポイントソース
      this.map.addSource("sdfMonitoringVesselPositionSource", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      // 監視船の航跡ソース
      this.map.addSource("sdfMonitoringVesselTrackSource", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      // 定点
      this.map.addSource("sdfSeaRouteSource", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      // 他船
      this.map.addSource("sdfOtherVesselsPositionSource", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      // 着岸モードの現在位置
      this.map.addSource("sdfCurrentGhostSymbolSource", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      // 着岸モードの過去位置
      this.map.addSource("sdfPastGhostSymbolSource", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });

      // 仮想岸壁
      this.map.addSource("sdfVirtualQuaySymbolSource", {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });
    },

    addLayers() {
      /*=========================================================================================
       航路 レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: "sdfTrafficRouteMaritimeTrafficLineLayer",
        type: "line",
        source: "sdfTrafficRouteSource",
        "source-layer": "maritime_traffic_regulations",
        paint: {
          "line-color": "#7f2a9f"
        },
      });

      this.map.addLayer({
        id: "sdfTrafficRouteMaritimeTrafficFillLayer",
        type: "fill",
        source: "sdfTrafficRouteSource",
        "source-layer": "maritime_traffic_regulations",
        paint: {
          'fill-color': '#7f2a9f',
          'fill-opacity': 0.5
        },
      });

      this.map.addLayer({
        id: "sdfTrafficRouteMaritimeTrafficSymbolLayer",
        type: "symbol",
        minzoom: 5,
        maxzoom: 12,
        source: "sdfTrafficRouteSource",
        "source-layer": "maritime_traffic_regulations",
        layout: {
          "text-field": "{name_jp}",
          "text-size": 14,
        },
        paint: {
          "text-color": "#c277de",
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });

      this.map.addLayer({
        id: "sdfTrafficRoutePortTrafficLineLayer",
        type: "line",
        source: "sdfTrafficRouteSource",
        "source-layer": "port_traffic_regulations",
        paint: {
          "line-color": "#7f2a9f"
        },
      });

      this.map.addLayer({
        id: "sdfTrafficRoutePortTrafficFillLayer",
        type: "fill",
        source: "sdfTrafficRouteSource",
        "source-layer": "port_traffic_regulations",
        paint: {
          'fill-color': '#7f2a9f',
          'fill-opacity': 0.5
        },
      });

      this.map.addLayer({
        id: "sdfTrafficRoutePortTrafficSymbolLayer",
        type: "symbol",
        minzoom: 5,
        maxzoom: 12,
        source: "sdfTrafficRouteSource",
        "source-layer": "port_traffic_regulations",
        layout: {
          "text-field": "{name_jp}",
          "text-size": 14,
        },
        paint: {
          "text-color": "#c277de",
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });

      /*=========================================================================================
      航路標識 レイヤー
     ==========================================================================================*/
      this.map.addLayer({
        id: "sdfNavigationalAidsLayer",
        type: "symbol",
        source: "sdfNavigationalAidsSource",
        "source-layer": "aisea_navigational_aids",
        layout: {
          "icon-image": "{icon}",
          "text-field": "{name_jp}",
          "text-anchor": "top",
          "text-size": 12,
          "text-offset": [0, 1.1]
        },
        paint: {
          "text-color": "#ffffff",
          "text-halo-color": "#000000",
          "text-halo-width": 1,
        }
      });

      /*=========================================================================================
       定点 レイヤー
      ==========================================================================================*/
      // 定点ラインレイヤー
      this.map.addLayer({
        id: "sdfSeaRouteLineLayer",
        type: "line",
        source: "sdfSeaRouteSource",
        paint: {
          "line-width": 2.0,
          "line-color": {
            type: "identity",
            property: "color"
          }
        },
        filter: ["==", "isDottedLine", false]
      });
      this.map.addLayer({
        id: "sdfSeaRouteDottedLineLayer",
        type: "line",
        source: "sdfSeaRouteSource",
        paint: {
          "line-width": 2.0,
          "line-color": {
            type: "identity",
            property: "color"
          },
          "line-dasharray": [1, 1]
        },
        filter: ["==", "isDottedLine", true]
      });

      // 定点ポイントレイヤー
      this.map.addLayer({
        id: "sdfSeaRoutePointLayer",
        type: "symbol",
        source: "sdfSeaRouteSource",
        layout: {
          "icon-image": "{icon}"
        }
      });

      // 定点テキストレイヤー
      this.map.addLayer({
        id: "sdfSeaRouteTextLayer",
        type: "symbol",
        source: "sdfSeaRouteSource",
        layout: {
          "text-field": "{routeName}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1]
        },
        paint: {
          "text-color": {
            type: "identity",
            property: "nameColor"
          },
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });

      /*=========================================================================================
       他船 レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: "sdfOtherVesselPositionSymbolLayer",
        type: "symbol",
        source: "sdfOtherVesselsPositionSource",
        layout: {
          "icon-allow-overlap": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-rotate": {
            type: "identity",
            property: "heading"
          }
        }
      });

      this.map.addLayer({
        id: "sdfOtherVesselPositionTextLayer",
        type: "symbol",
        source: "sdfOtherVesselsPositionSource",
        layout: {
          "icon-allow-overlap": true,
          "text-field": "{vesselName}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 1.1]
        },
        paint: {
          "text-color": this.sdfColor.White,
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });

      /*=========================================================================================
       監視船の航跡レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: "sdfMonitoringVesselTrackLineLayer",
        type: "line",
        source: "sdfMonitoringVesselTrackSource",
        paint: {
          "line-color": this.sdfColor.Yellow,
          "line-width": 2.0,
          "line-dasharray": [1, 1]
        }
      });

      /*=========================================================================================
        監視船 レイヤー
      ==========================================================================================*/
      // 監視船の円レイヤー
      this.crateMonitoringVesselCircle(this.map);
      this.map.addImage("monitoringVesselCircle", this.monitoringVesselCircle, {
        pixelRatio: 2
      });
      this.map.addLayer({
        id: "sdfMonitoringVesselPositionCircleLayer",
        type: "symbol",
        source: "sdfMonitoringVesselPositionSource",
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-image": "monitoringVesselCircle"
        }
      });

      // 監視船のポイントレイヤー
      this.map.addLayer({
        id: "sdfMonitoringVesselPositionLayer",
        type: "symbol",
        source: "sdfMonitoringVesselPositionSource",
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "sdf-track-vessel-15",
          "icon-rotate": {
            type: "identity",
            property: "vesselHeading"
          }
        },
        paint: {}
      });

      /*=========================================================================================
        着岸レイヤー
      ==========================================================================================*/
      this.map.addLayer({
        id: "sdfPastGhostLayer",
        type: "symbol",
        source: "sdfPastGhostSymbolSource",
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            0.05,
            14,
            0.125,
            15,
            0.25,
            16,
            0.5,
            17,
            1,
            18,
            2
          ],
          "icon-offset": [-25, 70],
          "icon-rotate": {
            type: "identity",
            property: "heading"
          }
        }
      });

      this.map.addLayer({
        id: "sdfPastGhostTextLayer",
        type: "symbol",
        source: "sdfPastGhostSymbolSource",
        layout: {
          "icon-allow-overlap": true,
          "text-field": "{timeDiff}",
          "text-anchor": "top",
          "text-size": 14,
          "text-line-height": 1.2,
          "text-offset": [0, 0]
        },
        paint: {
          "text-color": this.sdfColor.White,
          "text-halo-color": "#000000",
          "text-halo-width": 1
        }
      });

      this.map.addLayer({
        id: "sdfCurrentGhostLayer",
        type: "symbol",
        source: "sdfMonitoringVesselPositionSource",
        layout: {
          "icon-allow-overlap": true,
          "icon-ignore-placement": true,
          "icon-pitch-alignment": "map",
          "icon-rotation-alignment": "map",
          "icon-image": "{icon}",
          "icon-size": [
            "interpolate",
            ["linear"],
            ["zoom"],
            10,
            0.05,
            14,
            0.125,
            15,
            0.25,
            16,
            0.5,
            17,
            1,
            18,
            2
          ],
          "icon-offset": [-25, 70],
          "icon-rotate": {
            type: "identity",
            property: "vesselHeading"
          }
        }
      });

      /*=========================================================================================
       仮想岸壁
      ==========================================================================================*/
      this.map.addLayer({
        id: "sdfVirtualQuayLineLayer",
        type: "line",
        source: "sdfVirtualQuaySymbolSource",
        paint: {
          "line-color": this.sdfColor.Blue,
          "line-width": 3.0
        }
      });
    },
    setCursor(isPointer, self) {
      self.map.getCanvas().style.cursor = isPointer ? "pointer" : "";
    },
    setPointerStyle() {
      let self = this;
      this.map.on("mousemove", "sdfMonitoringVesselPositionLayer", () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", "sdfMonitoringVesselPositionLayer", () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", "sdfCurrentGhostLayer", () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", "sdfCurrentGhostLayer", () => {
        self.setCursor(false, self);
      });
      this.map.on("mousemove", "sdfSeaRoutePointLayer", () => {
        self.setCursor(true, self);
      });
      this.map.on("mouseleave", "sdfSeaRoutePointLayer", () => {
        self.setCursor(false, self);
      });
    },
    bindLayerClick() {
      let self = this;
      let onMonitoringVessel = async e => {
        this.showPopupForMonitoringVessel(self, e);
      };

      this.map.on(
        "click",
        "sdfMonitoringVesselPositionLayer",
        onMonitoringVessel
      );

      let onCurrentGhost = async () => {
        this.showPopupForGhost(self);
      };
      this.map.on("click", "sdfCurrentGhostLayer", onCurrentGhost);

      let onSeaRoute = async e => {
        this.showPopupForSeaRoute(e.features[0].properties.routeNumber);
      };
      this.map.on("click", "sdfSeaRoutePointLayer", onSeaRoute);
    },

    updateMonitoringVesselPositionSource(byZoom) {
      if (!this.map) return
      if (this.popupForMonitoringVessel !== null) {
        this.popupForMonitoringVessel.remove();
        this.showPopupForMonitoringVessel(this);
      }
      this.map.setLayoutProperty(
        "sdfMonitoringVesselPositionLayer",
        "visibility",
        this.isSteadyOrInland ? "visible" : "none"
      );
      this.map.setLayoutProperty(
        "sdfMonitoringVesselPositionCircleLayer",
        "visibility",
        this.isSteadyOrInland ? "visible" : "none"
      );
      this.map.setLayoutProperty(
        "sdfCurrentGhostLayer",
        "visibility",
        !this.isSteadyOrInland ? "visible" : "none"
      );

      let vesselInfo = this.getMonitoringVesselInfo;
      const monitoringVesselSource = this.map.getSource(
        "sdfMonitoringVesselPositionSource"
      );
      if (monitoringVesselSource != null) {
        monitoringVesselSource.setData(
          this.getMonitoringVesselPositionFeatureCollection
        );
      }

      if(byZoom) return;
      this.MERGE_TRACK_POSITIONS(vesselInfo);

      // 定常航海 or 内海以外はゴーストを作成
      if (!this.isSteadyOrInland) {
        if (this.ghostPositions.length !== 0) {
          const lastGhostPosition =
            this.ghostPositions[this.ghostPositions.length - 1];
          this.beforeDisplayGhostDatetime =
            lastGhostPosition.datetime > this.beforeDisplayGhostDatetime
              ? lastGhostPosition.datetime
              : this.beforeDisplayGhostDatetime;

          let dateTo = moment(vesselInfo.datetime);
          let dateFrom = moment(this.beforeDisplayGhostDatetime);
          let diff = dateTo.diff(dateFrom, "m");
          if (diff >= 1) {
            this.MERGE_GHOST_POSITIONS(vesselInfo);
            this.beforeDisplayGhostDatetime = vesselInfo.datetime;
          }
        } else {
          this.beforeDisplayGhostDatetime = vesselInfo.datetime;
          this.MERGE_GHOST_POSITIONS(vesselInfo);
        }
        // popupの差し替え
        if (this.popupForMonitoringVessel !== null) {
          this.popupForMonitoringVessel.remove();
          this.isShowPopupForMonitoringVessel = false;
          this.showPopupForGhost(this);
        } else if (this.popupForGhost !== null) {
          this.showPopupForGhost(this);
        }
      }

      if(vesselInfo.operationMode) {
        if (this.currentOperationMode !== vesselInfo.operationMode) {
          if(this.onVesselAutoTracking) {
            this.map.jumpTo({
              center: [vesselInfo.longitude, vesselInfo.latitude],
              zoom: this.getZoomLevelByOperationMode(vesselInfo.operationMode),
              pitch: this.map.getPitch(),
              bearing: this.map.getBearing()
            });
          }
          this.currentOperationMode = vesselInfo.operationMode
        }
        return
      }

      if (this.onVesselAutoTracking) {
        this.map.jumpTo({
          center: [vesselInfo.longitude, vesselInfo.latitude],
          zoom: this.getZoomLevelByOperationMode(vesselInfo.operationMode),
          pitch: this.map.getPitch(),
          bearing: this.map.getBearing()
        });
      }

      // if (this.resetMap) {
      //   this.resetMap = false;
      //   this.map.jumpTo({
      //     center: [vesselInfo.longitude, vesselInfo.latitude],
      //     zoom: 14
      //   });
      // }
    },

    updateTrackSource() {
      const trackSource = this.map.getSource("sdfMonitoringVesselTrackSource");
      if (trackSource != null) {
        trackSource.setData(this.getTrackLineFeatureCollection);
      }
    },

    async addOtherVessels() {
      this.SET_OTHER_VESSELS_POSITION(null)
      this.fetchOtherVesselsPosition({
        coordinates: this.getMonitoringVesselGeoPoint,
        myVesselId: this.getVesselId
      });
      let self = this
      let intervalId = this.$setInterval(() => {
        self.fetchOtherVesselsPosition(
          {
            coordinates: self.getMonitoringVesselGeoPoint,
            myVesselId: self.getVesselId
          }
        );
      }, 3500 * 10);
      this.intervalIds.push(intervalId);
    },

    updateOtherVessels() {
      if (!this.map) return;
      const positionSource = this.map.getSource(
        "sdfOtherVesselsPositionSource"
      );
      if (positionSource != null) {
        positionSource.setData(this.getOtherVesselsPosition);
      }
    },

    clearOtherVessels() {
      if (!this.map) return;
      const positionSource = this.map.getSource(
        "sdfOtherVesselsPositionSource"
      );
      if (positionSource != null) {
        positionSource.setData({
          type: "FeatureCollection",
          features: []
        });
      }
    },

    setVisibilityOtherVessels() {
      if (!this.map) return;
      this.map.setLayoutProperty(
        "sdfOtherVesselPositionSymbolLayer",
        "visibility",
        this.isDisplayOtherVessels ? "visible" : "none"
      );

      this.map.setLayoutProperty(
        "sdfOtherVesselPositionTextLayer",
        "visibility",
        this.isDisplayOtherVessels ? "visible" : "none"
      );
    },

    updateSeaRoute() {
      if (!this.map) return
      const seaRouteSource = this.map.getSource("sdfSeaRouteSource");
      if (seaRouteSource) {
        let featureCollection = this.getSeaRouteFeatureCollection;
        if (featureCollection) {
          seaRouteSource.setData(this.getSeaRouteFeatureCollection);
        }
      }
      if (!this.initialized) {
        this.closeLoading();
      }
    },

    showPopupForMonitoringVessel(self, evt) {
      this.isShowPopupForMonitoringVessel = true;
      if (this.popupForMonitoringVessel) {
        this.popupForMonitoringVessel.remove();
      }
      this.popupForMonitoringVessel = new Mapbox.Popup({
        offset: 25,
        maxWidth: "500px",
        className: "sdf-live-map-popup-for-monitoring-vessel",
        toggleClassName: "sdf-live-map-popup-toggle",
        closeOnClick: false
      });
      self.updatePopupForMonitoringVessel();
      this.popupForMonitoringVessel.on("close", () => {
        self.popupForMonitoringVessel = null;
      });
    },

    updatePopupForMonitoringVessel() {
      let info = this.getMonitoringVesselInfo;
      let coordinates = [info.longitude, info.latitude];
      let operationMode = this.getOperationModeLabel(info.operationMode);
      let steeringMode = this.getSteeringModeLabel(info.steeringMode);
      let maneuveringMode = this.getManeuveringModeLabel(info.maneuveringMode);
      let vesselHeading = `${info.vesselHeading.toFixed(0)}°`;
      let speedOverGround = `${info.speedOverGroundPrecise.toFixed(1)}kt`;

      let description = `<ul class="sdf-text-16-400-white popup">
<li>運航モード：${operationMode}</li>
<li>操舵モード：${steeringMode}</li>
<li>操縦モード：${maneuveringMode}</li>
<li>方位：${vesselHeading}</li>
<li>速力：${speedOverGround}</li>
</ul>`;

      this.popupForMonitoringVessel
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(this.map);

      setTimeout(() => {
        this.isShowPopupForMonitoringVessel = false;
      }, 1000);
    },

    updateGhostSymbolSource() {
      let featureCollection = this.getGhostPositionsFeatureCollection;
      if (featureCollection.length === 0) return
      const ghostSymbolSource = this.map.getSource("sdfPastGhostSymbolSource");
      if (ghostSymbolSource) {
        ghostSymbolSource.setData(featureCollection);
      }
    },

    setVisibilityGhost() {
      if (!this.map) return;
      this.map.setLayoutProperty(
        "sdfPastGhostTextLayer",
        "visibility",
        this.trackDisplayInterval === 60 ? "visible" : "none"
      );
      this.map.setLayoutProperty(
        "sdfPastGhostLayer",
        "visibility",
        this.trackDisplayInterval === 60 ? "visible" : "none"
      );
    },

    showPopupForGhost(self) {
      if (this.popupForMonitoringVessel !== null) {
        this.popupForMonitoringVessel.remove();
        this.isShowPopupForMonitoringVessel = false;
      }
      if (this.popupForGhost) {
        this.popupForGhost.remove();
      }
      this.popupForGhost = new Mapbox.Popup({
        offset: 25,
        maxWidth: "500px",
        className: "sdf-live-map-popup-for-monitoring-vessel",
        toggleClassName: "sdf-live-map-popup-toggle"
      });
      self.updatePopupForGhost();
      this.popupForGhost.on("close", () => {
        self.popupForGhost = null;
      });
    },

    updatePopupForGhost() {
      let info = this.getMonitoringVesselInfo;
      let coordinates = [info.longitude, info.latitude];
      let distanceFromBowToBerth = `${Number(
        info.distanceFromBowToBerth
      ).toFixed(1)}m`;
      let distanceFromSternToBerth = `${Number(
        info.distanceFromSternToBerth
      ).toFixed(1)}m`;
      let operationMode = this.getOperationModeLabel(info.operationMode);
      let steeringMode = this.getSteeringModeLabel(info.steeringMode);
      let maneuveringMode = this.getManeuveringModeLabel(info.maneuveringMode);
      let vesselHeading = `${Number(info.vesselHeading).toFixed(1)}°`;
      let speedOverGround = `${Number(info.speedOverGroundPrecise).toFixed(
        1
      )}kt`;

      let description;
      if (this.idDepartureOrBerthing) {
        description = `<ul class="popup">
<div class="sdf-text-18-400-lemon-yellow">
<li>船首距離：${distanceFromBowToBerth}</li>
<li>船尾距離：${distanceFromSternToBerth}</li>
</div>
<div class="sdf-text-16-400-white">
<li>運航モード：${operationMode}</li>
<li>操舵モード：${steeringMode}</li>
<li>操縦モード：${maneuveringMode}</li>
<li>方位：${vesselHeading}</li>
<li>速力：${speedOverGround}</li>
</div>
</ul>`;
      } else {
        description = `<ul class="popup">
<div class="sdf-text-16-400-white">
<li>運航モード：${operationMode}</li>
<li>操舵モード：${steeringMode}</li>
<li>操縦モード：${maneuveringMode}</li>
<li>方位：${vesselHeading}</li>
<li>速力：${speedOverGround}</li>
</div>
</ul>`;
      }

      this.popupForGhost
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(this.map);
    },

    showPopupForSeaRoute(routeNumber) {
      if (this.isShowPopupForMonitoringVessel) return; // 自船と重なっている時は出さない
      if (this.popupForSeaRoutes[routeNumber]) {
        this.popupForSeaRoutes[routeNumber].remove();
        this.popupForSeaRoutes[routeNumber] = null;
      }
      let info = this.getSeaRouteInfo(routeNumber);
      if (!info) return;
      let coordinates = info.coordinates;
      let description;
      let className;
      if (info) {
        description = `<ul class="popup">
<div class="sdf-text-16-400-white">
<li>到着目標日時：${info.passingEstimatedTime}</li>
<li>到着実績日時：${info.passingTime || "---"}</li>
</div>
</ul>`;
        className = "sdf-live-map-popup-for-sae-route-passing";
      } else {
        if (info) {
          description = `<ul class="sdf-text-14-400-white sea-route-tooltip-contents">
<li>到着目標日時：${info.passingEstimatedTime}</li>
<li>運航モード：${info.operationMode}</li>
<li>変針方法：${info.veeringMethod}</li>
<li>ターンレート：${info.turnRate}</li>
</ul>`;
        } else if (info) {
          description = `<ul class="sdf-text-14-400-white sea-route-tooltip-contents">
<li>到着目標日時：${info.passingEstimatedTime}</li>
<li>運航モード：${info.operationMode}</li>
<li>変針方法：${info.veeringMethod}</li>
<li>旋回径：${info.turnRadius}</li>
</ul>`;
        }
        className = "sdf-live-map-popup-for-sae-route-not-passing";
      }
      this.popupForSeaRoutes[routeNumber] = new Mapbox.Popup({
        offset: 10,
        maxWidth: "500px",
        className: className,
        toggleClassName: "sdf-live-map-popup-toggle",
        closeOnClick: false
      });

      this.popupForSeaRoutes[routeNumber]
        .setLngLat(coordinates)
        .setHTML(description)
        .addTo(this.map);
    },

    crateMonitoringVesselCircle(map) {
      let size = 200;
      this.monitoringVesselCircle = {
        width: size,
        height: size,
        data: new Uint8Array(size * size * 4),
        onAdd: function () {
          let canvas = document.createElement("canvas");
          canvas.width = this.width;
          canvas.height = this.height;
          this.context = canvas.getContext("2d");
        },

        render: function () {
          let duration = 3000;
          let t = (performance.now() % duration) / duration;

          let radius = (size / 2) * 0.3;
          let outerRadius = (size / 2) * 0.2 * t + radius;
          let context = this.context;

          context.clearRect(0, 0, this.width, this.height);
          context.beginPath();
          context.arc(
            this.width / 2,
            this.height / 2,
            outerRadius,
            0,
            Math.PI * 2
          );
          context.fillStyle = "rgba(249, 254, 12," + (1 - t) + ")";
          context.fill();

          // draw inner circle
          context.beginPath();
          context.arc(this.width / 2, this.height / 2, radius, 0, Math.PI * 2);
          context.fillStyle = "rgba(249, 254, 12, 0.4)";
          context.strokeStyle = "#F9FE0C";
          context.lineWidth = 2 + 4 * (1 - t);
          context.fill();
          context.stroke();

          this.data = context.getImageData(0, 0, this.width, this.height).data;

          map.triggerRepaint();

          return true;
        }
      };
    },
    updateMapBoxCtrlStyle() {
      if (!this.map) return;
      let bottomLeftEl = document.getElementsByClassName(
        "mapboxgl-ctrl-bottom-left"
      );
      let bottomRighttEl = document.getElementsByClassName(
        "mapboxgl-ctrl-bottom-right"
      );
      if (this.onAlertSummaryNotification) {
        bottomLeftEl[0].lastChild.classList.remove("margin-10");
        bottomLeftEl[0].lastChild.classList.add("margin-100");
        bottomRighttEl[0].lastChild.classList.remove("margin-10");
        bottomRighttEl[0].lastChild.classList.add("margin-100");
      } else {
        bottomLeftEl[0].lastChild.classList.remove("margin-100");
        bottomLeftEl[0].lastChild.classList.add("margin-10");
        bottomRighttEl[0].lastChild.classList.remove("margin-100");
        bottomRighttEl[0].lastChild.classList.add("margin-10");
      }
    },

    setVirtualQuay() {
      if (!this.map) return;
      const virtualQuaySymbolSource = this.map.getSource(
        "sdfVirtualQuaySymbolSource"
      );
      if (virtualQuaySymbolSource) {
        virtualQuaySymbolSource.setData(this.getVirtualQuayFeatureCollection);
      }
    },

    updateMapStyle() {
      let el = document.getElementsByClassName(
        "mapboxgl-ctrl-map-style-changer"
      );
      if (this.mapStyleType === 1) {
        el[0].lastChild.src = this.imageSatellite;
        this.mapStyleType = 2;
        this.map.setStyle(MAP_STYLE_DEFAULT);
      } else {
        el[0].lastChild.src = this.imageDefault;
        this.mapStyleType = 1;
        this.map.setStyle(MAP_STYLE_SATELLITE_SECURE_DATAFLOW);
      }
    },

    async cleanup(){
      this.intervalIds.forEach(v => {
        this.$clearInterval(v);
      });

      this.mapLayerIds.forEach(layerId => {
        if (this.map.getLayer(layerId)) {
          this.map.removeLayer(layerId);
        }
      })

      this.mapSourceIds.forEach(sourceId => {
        if (this.map.getSource(sourceId)) {
          this.map.removeSource(sourceId);
        }
      })
    },
    async getHandlingDataForGhost() {
      // 航跡・ハンドリング情報の表示に使用するチャンネル
      const channelIds = [
        "SB02",
        "SB03",
        "SB04",
        "SB05",
        "SB06",
        "SB07",
        "SB08",
        "SB09",
        "SB10",
        "SB11",
        "SB12",
        "SB13",
        "SB14",
        "SB15",
        "SB16",
        "SB17",
        "SB18",
        "SB19",
        "SB20",
        "SB21",
        "SB22",
        "SB23",
        "SB24",
        "SB25",
        "SB26",
        "SB27",
        "SB28",
        "SB29",
        "SB30",
        "SB31",
        "SB32",
        "SB33",
        "SB34",
        "SB35",
        "SB36",
        "SB37",
        "SB38",
        "SB39",
        "SB40",
        "SB41",
        "SB42",
        "SB43",
        "SB44",
        "SB45",
        "SB46",
      ];
      const jumpingGhostPostion =
        this.ghostPositions[this.junmpingGhostPosition];
      const from = moment(jumpingGhostPostion.datetime);
      const to = moment().utc();
      const fromISOString = from.toISOString();
      const toISOString = to.toISOString();
      const vesselId = this.getVesselId;
      const { originId, versionId, versionName } = this.getVersion.handling;
      const params = {
        vessel_id: vesselId,
        body: {
          channel_ids: channelIds,
          kind: "handling",
          // origin: 2,
          // version_id: "19",
          // version_name: "MAN_K5",
          origin: originId,
          version_id: versionId,
          version_name: versionName,
          channel_time_from: fromISOString,
          channel_time_to: toISOString,
        },
      };

      const result = epVesselSecureDataflowChannel.searchChannelsData(
        vesselId,
        params.body
      );
      return result;
    },
    async updateHandlingData() {
      if (this.isPositionsUpdatingByApi) return;
      if (this.isSteadyOrInland) return;
      if (!this.beforeDisplayGhostDatetime) return;

      this.SET_BEFORE_UPDATING_GHOST_BY_API_DATETIME(moment.utc());
      if (!this.isGhosToBeUpdated) {
        this.SET_BEFORE_UPDATING_GHOST_BY_API_DATETIME(null);
        return;
      }
      this.SET_IS_POSITIONS_UPDATING_BY_API(true);
      this.getHandlingDataForGhost()
        .then((res) => {
          const responseData = (res && res.data && res.data.data) || [];
          this.updateGhostPoistions(responseData);
        })
        .catch(() => {
          this.$log.debug("Failed searchChannelsData API");
        })
        .finally(() => {
          setTimeout(() => {
            this.SET_IS_POSITIONS_UPDATING_BY_API(false);
            this.SET_BEFORE_UPDATING_GHOST_BY_API_DATETIME(null);
          });
        });
    },
    setIntervalUpdateanHandlingData() {
      const intervalTime = 60 * 1000; // 1分
      let self = this;
      let intervalId = this.$setInterval(() => {
        self.updateHandlingData();
      }, intervalTime);
      this.intervalIds.push(intervalId);
    },
  },
  async created() {
    const {zoom_level: zoomLevel} = await SecureDataflowService.getMap();
    this.zoomLevel = zoomLevel;
  },
  mounted() {
  },
  beforeDestroy() {
    this.intervalIds.forEach(v => {
      this.$clearInterval(v);
    });
  }
};
</script>

<style lang="scss">
@import "@/assets/scss/secure-dataflow/monitoringVesselManeuvering/liveMap.scss";
</style>
